import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pmsKeys } from '../queryKeys';
import { teamApi } from './team.api';
import type { SaveAnswersPayload, TeamEvaluationListParams } from '../types';

/**
 * FR-401 — the signed-in manager's evaluation queue.
 *
 * Short staleTime: a row's stage flips the moment its employee submits, and a
 * manager watching the list expects it to become actionable without a reload.
 */
export function usePmsTeamEvaluations(params?: TeamEvaluationListParams) {
  return useQuery({
    queryKey: pmsKeys.teamEvaluations(params),
    queryFn: () => teamApi.list(params),
    staleTime: 30_000,
    /**
     * `'always'`, not `true` — the row is edited in ANOTHER TAB.
     *
     * "Start evaluation" opens the observer form with `target="_blank"`, so the
     * autosave that moves a participant to `RMInProgress` runs in a separate tab
     * with its own React Query cache. Its `invalidateQueries` can never reach
     * this one, which is why filling half a form and coming back still offered
     * "Start evaluation" instead of "Continue evaluation".
     *
     * Plain `true` (the default) would not fix it: it refetches on focus only
     * when the query is already STALE, and `staleTime` keeps it fresh for 30s —
     * so the bug looked intermittent, correcting itself if you took long enough
     * over the form. `'always'` refetches whenever this tab is returned to,
     * which is exactly when a change made elsewhere needs picking up.
     *
     * `staleTime` stays: it still stops remounts within the page from refetching.
     */
    refetchOnWindowFocus: 'always',
  });
}

/**
 * One participant's observer form (FR-402/403).
 *
 * `staleTime: 0` deliberately: `locked_reason` and `counterpart_answers` change
 * the moment the employee submits, and showing a stale "waiting on employee" over
 * a form that is now open is worse than an extra fetch.
 */
export function usePmsTeamEvaluation(participantId: string | undefined) {
  return useQuery({
    queryKey: pmsKeys.teamEvaluation(participantId ?? ''),
    queryFn: () => teamApi.get(participantId as string),
    enabled: !!participantId,
    staleTime: 0,
  });
}

/**
 * Autosave.
 *
 * The server returns the whole refreshed detail, so it is written straight into
 * the cache rather than triggering a refetch — a save on every click would
 * otherwise mean two round trips per answer.
 */
export function useSavePmsTeamAnswers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      participantId,
      data,
    }: {
      participantId: string;
      data: SaveAnswersPayload;
    }) => teamApi.saveAnswers(participantId, data),
    onSuccess: (detail, { participantId }) => {
      qc.setQueryData(pmsKeys.teamEvaluation(participantId), detail);
      // The queue's stage/state moved with the first answer.
      qc.invalidateQueries({ queryKey: [...pmsKeys.all, 'team', 'evaluations'] });
    },
  });
}

export function useRequestPmsTeamOtp() {
  return useMutation({
    mutationFn: (participantId: string) => teamApi.requestOtp(participantId),
  });
}

export function useSubmitPmsTeamEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      participantId,
      otp,
      dpdpConsent,
    }: {
      participantId: string;
      otp: string;
      dpdpConsent: boolean;
    }) => teamApi.submit(participantId, otp, dpdpConsent),
    onSuccess: (_r, { participantId }) => {
      // Both the form (now locked, and the employee's answers now visible) and
      // the queue (now "done") are stale.
      qc.invalidateQueries({ queryKey: pmsKeys.teamEvaluation(participantId) });
      qc.invalidateQueries({ queryKey: [...pmsKeys.all, 'team', 'evaluations'] });
      // A submission moves the dashboard's completion figures.
      qc.invalidateQueries({ queryKey: pmsKeys.dashboardAll() });
    },
  });
}
